import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AddressForm, { emptyAddress, isAddressComplete, validateUaeMobile, UaeMobileInput, MOBILE_ERROR } from "@/components/AddressForm";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ name: "", email: "", password: "", mobile: "" });
  const [address, setAddress] = useState(emptyAddress);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [mobileErr, setMobileErr] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const goStep2 = () => {
    if (!f.name || !f.email || !f.password) { toast.error("Please fill all fields"); return; }
    const m = validateUaeMobile(f.mobile);
    if (!m.ok) { setMobileErr(MOBILE_ERROR); return; }
    setMobileErr("");
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isAddressComplete(address)) { toast.error("Please complete your address"); return; }
    if (!termsAccepted) { toast.error("Please accept the Terms & Conditions and Privacy Policy"); return; }
    const m = validateUaeMobile(f.mobile);
    if (!m.ok) { setStep(1); setMobileErr(MOBILE_ERROR); return; }
    setLoading(true);
    try {
      await register({ ...f, mobile: m.normalized, emirate: "Dubai", area: address.area, address, user_type: "both", language_preference: "English", marketing_consent: marketingConsent, terms_accepted: termsAccepted });
      navigate("/verify");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Create your account</h1>
        <p className="mb-6 text-sm text-slate-500">Step {step} of 2 — {step === 1 ? "your details" : "your address"}</p>

        <form onSubmit={submit} className="space-y-4">
          {step === 1 ? (
            <>
              <Field label="Full name" required><Input data-testid="reg-name" value={f.name} onChange={set("name")} required className="rounded-lg" /></Field>
              <Field label="Email" required><Input data-testid="reg-email" type="email" value={f.email} onChange={set("email")} required className="rounded-lg" /></Field>
              <Field label="Password" required><Input data-testid="reg-password" type="password" value={f.password} onChange={set("password")} required minLength={6} className="rounded-lg" /></Field>
              <Field label="UAE mobile (WhatsApp)" required>
                <UaeMobileInput testid="reg-mobile" value={f.mobile} onChange={(v) => { setF({ ...f, mobile: v }); if (mobileErr) setMobileErr(""); }} />
                {mobileErr && <p className="mt-1 text-xs text-red-500" data-testid="reg-mobile-error">{mobileErr}</p>}
              </Field>
              <Button type="button" data-testid="reg-next" onClick={goStep2} className="w-full rounded-full bg-primary font-semibold hover:bg-primary/90">Continue</Button>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-slate-900">Where should we collect and deliver?</h2>
              <AddressForm value={address} onChange={setAddress} />
              <div className="rounded-xl bg-blue-50 p-3 text-xs text-blue-800">
                Sellers pay a refundable AED 99 security deposit. It covers fees such as failed collections or cancellations, as set out in our Terms.
              </div>
              <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-600" data-testid="reg-terms-label">
                <input type="checkbox" data-testid="reg-terms-accept" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary" />
                <span>I agree to SellStuff's <Link to="/terms" target="_blank" className="font-semibold text-primary underline">Terms &amp; Conditions</Link> and <Link to="/privacy" target="_blank" className="font-semibold text-primary underline">Privacy Policy</Link>.</span>
              </label>
              <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-600" data-testid="reg-marketing-consent-label">
                <input type="checkbox" data-testid="reg-marketing-consent" checked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary" />
                <span>Email me when items I might like are listed, plus occasional updates from SellStuff.</span>
              </label>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-full">Back</Button>
                <Button type="submit" disabled={loading || !isAddressComplete(address) || !termsAccepted} data-testid="reg-submit" className="flex-1 rounded-full bg-primary font-semibold hover:bg-primary/90">{loading ? "Creating…" : "Complete signup"}</Button>
              </div>
            </>
          )}
        </form>
        <p className="mt-6 text-center text-sm text-slate-500">Already have an account? <Link to="/login" className="font-semibold text-primary">Log in</Link></p>
      </div>
    </div>
  );
}

const Field = ({ label, children, required }) => (
  <div><Label className="mb-1 block">{label}{required && <span className="text-red-500"> *</span>}</Label>{children}</div>
);
